import { ArrowUp, Loader2, Mic } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import {
    EmojiEnabledTextarea,
    resizeMessageTextarea,
} from '@apps/web/components/shared/messages/emoji_composer_support';
import {
    MESSAGE_INPUT_LIMIT_ERROR,
    MESSAGE_MAX_LENGTH,
} from '@apps/web/components/shared/messages/message_composer_constants';
import {
    messageAndImagesFitMms,
    MessageImageUploadControls,
    useMessageImageUploads,
} from '@apps/web/components/shared/messages/message_image_uploads';
import {
    fetchOutboundTranslationPreview,
    TranslationPreviewDialog,
} from '@apps/web/components/shared/messages/translation_preview_dialog';
import {
    appendTranscribedText,
    getPreferredRecordingMimeType,
    isMicrophonePermissionError,
    MAX_RECORDING_DURATION_MS,
    transcribeAudioBlob,
    VOICE_INPUT_ERROR_MESSAGE,
    VOICE_INPUT_PERMISSION_ERROR_MESSAGE,
} from '@apps/web/components/shared/messages/voice_input_utils';
import { Button } from '@apps/web/components/ui/button';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { EVENTS } from '@apps/web/lib/events';
import { useMode } from '@apps/web/lib/mode_store';
import { cn } from '@apps/web/lib/utils';

type MessageComposerProps = {
    readonly timezone: string;
    readonly organizationId: string;
    // '' while the composer is a draft (see `draft` below).
    readonly conversationId: string;
    // Draft mode: the inbox's new-message row has a recipient but no thread
    // yet, and the API creates the conversation and its first message in one
    // request. The send call is the only thing that differs, so it is the only
    // thing delegated -- uploads, MMS sizing, the character limit, voice input
    // and the translation preview all stay exactly as they are in a real
    // thread. Resolving to null leaves the draft in the box; the caller has
    // already told the user why.
    readonly draft?: {
        // A real thread keys its translation preview by conversation; a draft
        // has only its recipient, and a phone-number recipient has no
        // caregiver to translate for.
        readonly caregiverId: string | null;
        // Stable recipient identity for the draft preview/send boundary.
        // conversationId is '' in draft mode, so recipient changes must bump
        // the same generation counter a thread switch would.
        readonly recipientKey: string;
        readonly sendDisabled?: boolean;
        readonly onSend: (message: {
            readonly content: string;
            readonly englishSource: string | null;
            readonly attachmentIds: ReadonlyArray<string>;
        }) => Promise<string | null>;
    };
    readonly analyticsSurface?: MessageComposerSurface;
    readonly onSent?: () => void | Promise<void>;
    // Fired on each keystroke that leaves non-empty content, to drive the
    // multiplayer "is typing…" presence indicator for this conversation.
    readonly onTyping?: () => void;
    // True while send/preview (or upload/record/transcribe) locks the composer,
    // so a draft parent can disable its recipient picker.
    readonly onBusyChange?: (busy: boolean) => void;
};

type VoiceInputStopReason = 'manual' | 'max_duration';
export type MessageComposerSurface = 'extension' | 'web' | 'activity_feed';

function detectMessageComposerSurface(): MessageComposerSurface {
    if (
        typeof window !== 'undefined' &&
        window.location.pathname.startsWith('/extension')
    ) {
        return 'extension';
    }

    return 'web';
}

function getMessageComposerSource(
    surface: MessageComposerSurface,
): 'chrome_extension' | 'web_app' {
    return surface === 'extension' ? 'chrome_extension' : 'web_app';
}

// Only surface the character counter once the draft is actually nearing the
// limit — rendering it from the first keystroke made the composer grow a row
// the moment typing started.
const MESSAGE_COUNTER_VISIBLE_FROM = MESSAGE_MAX_LENGTH - 100;

export function MessageComposer({
    timezone,
    organizationId,
    conversationId,
    draft,
    analyticsSurface,
    onSent,
    onTyping,
    onBusyChange,
}: MessageComposerProps) {
    const posthog = usePostHog();
    const [content, setContent] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [translationPreview, setTranslationPreview] = useState<{
        readonly englishSource: string;
        readonly languageName: string | null;
        readonly translatedText: string;
    } | null>(null);
    const [canRecordAudio, setCanRecordAudio] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [composerInputError, setComposerInputError] = useState<string | null>(
        null,
    );
    const [voiceInputError, setVoiceInputError] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const restoreFocusAfterSendRef = useRef(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const audioChunksRef = useRef<Array<Blob>>([]);
    const recordingTimeoutRef = useRef<number | null>(null);
    const recordingStartedAtRef = useRef<number | null>(null);
    const recordingStopReasonRef = useRef<VoiceInputStopReason>('manual');
    const transcriptionGenerationRef = useRef(0);
    const draftIncludesVoiceInputRef = useRef(false);
    // Bumped when the conversation changes or the composer unmounts, so an
    // in-flight translate-preview that resolves afterward is discarded instead
    // of sending to / surfacing on the wrong thread.
    const previewGenerationRef = useRef(0);
    const mode = useMode();
    const {
        uploads,
        attachmentIds,
        isUploading,
        hasFailedUploads,
        totalSizeBytes,
        addFiles,
        clear: clearImages,
        handlePaste,
        removeUpload,
    } = useMessageImageUploads(organizationId);
    const handleEmojiInsertionLimit = useCallback(() => {
        setComposerInputError(MESSAGE_INPUT_LIMIT_ERROR);
    }, []);
    const handleComposerContentChange = useCallback(
        (nextContent: string) => {
            if (!nextContent.trim()) {
                draftIncludesVoiceInputRef.current = false;
            } else {
                onTyping?.();
            }
            setContent(nextContent);
        },
        [onTyping],
    );

    const draftRecipientKey = draft?.recipientKey ?? '';

    useEffect(() => {
        // Switching conversations (the composer is reused, not remounted) must
        // clear any preview carried from the prior thread and release a stale
        // sending lock; the cleanup invalidates an in-flight preview fetch.
        // Draft mode also keys this boundary on recipientKey because
        // conversationId stays '' while the recipient can still change.
        setIsSending(false);
        setTranslationPreview(null);
        setComposerInputError(null);
        setVoiceInputError(null);
        restoreFocusAfterSendRef.current = false;
        return () => {
            previewGenerationRef.current += 1;
        };
    }, [conversationId, draftRecipientKey]);

    // A recipient change retains the whole draft, including attached images.
    useEffect(() => {
        clearImages();
    }, [clearImages, conversationId]);

    useEffect(() => {
        onBusyChange?.(
            isSending ||
                translationPreview !== null ||
                isUploading ||
                isRecording ||
                isTranscribing,
        );
    }, [
        isRecording,
        isSending,
        isTranscribing,
        isUploading,
        onBusyChange,
        translationPreview,
    ]);

    const clearRecordingTimeout = useCallback(() => {
        if (recordingTimeoutRef.current === null) {
            return;
        }

        window.clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = null;
    }, []);

    const stopMediaStream = useCallback(() => {
        mediaStreamRef.current?.getTracks().forEach((track) => {
            track.stop();
        });
        mediaStreamRef.current = null;
    }, []);

    useEffect(() => {
        setCanRecordAudio(
            typeof MediaRecorder !== 'undefined' &&
                typeof navigator !== 'undefined' &&
                Boolean(navigator.mediaDevices?.getUserMedia),
        );
    }, []);

    useEffect(() => {
        return () => {
            clearRecordingTimeout();

            const recorder = mediaRecorderRef.current;
            if (recorder && recorder.state !== 'inactive') {
                recorder.ondataavailable = null;
                recorder.onstop = null;
                recorder.stop();
            }

            stopMediaStream();
        };
    }, [clearRecordingTimeout, stopMediaStream]);

    const doSend = useCallback(
        async (body: string, englishSource: string | null) => {
            if (!messageAndImagesFitMms(body, totalSizeBytes)) {
                toast.error('Message and images must be 5 MB or smaller', {
                    persistNotification: false,
                });
                return;
            }
            transcriptionGenerationRef.current += 1;
            const draftLength = content.trim().length;
            const includedVoiceInput = draftIncludesVoiceInputRef.current;
            const surface = analyticsSurface ?? detectMessageComposerSurface();
            const source = getMessageComposerSource(surface);
            setIsSending(true);
            try {
                let sentConversationId = conversationId;
                if (draft) {
                    const createdConversationId = await draft.onSend({
                        content: body,
                        englishSource,
                        attachmentIds,
                    });
                    if (createdConversationId === null) {
                        return;
                    }
                    sentConversationId = createdConversationId;
                } else {
                    await getApiClient().post({
                        url: '/organizations/{organization_id}/conversations/{conversation_id}/messages',
                        pathParams: {
                            organization_id: organizationId,
                            conversation_id: conversationId,
                        },
                        body: {
                            content: body,
                            english_source: englishSource ?? undefined,
                            ...(attachmentIds.length
                                ? { attachment_ids: attachmentIds }
                                : {}),
                        },
                    });
                }
                posthog?.capture(EVENTS.CONVERSATION_MESSAGE_SENT, {
                    composer: 'message_composer',
                    conversation_id: sentConversationId,
                    draft_length: draftLength,
                    included_voice_input: includedVoiceInput,
                    mode,
                    organization_id: organizationId,
                    source,
                    surface,
                    translated: englishSource !== null,
                });
                if (includedVoiceInput) {
                    posthog?.capture(EVENTS.VOICE_INPUT_MESSAGE_SENT, {
                        composer: 'message_composer',
                        conversation_id: sentConversationId,
                        draft_length: draftLength,
                        mode,
                        organization_id: organizationId,
                        source,
                        surface,
                        voice_input_flow: 'conversation_messages',
                    });
                }
                draftIncludesVoiceInputRef.current = false;
                setContent('');
                setComposerInputError(null);
                setVoiceInputError(null);
                clearImages();
                setTranslationPreview(null);
                resizeMessageTextarea(textareaRef.current);
                await onSent?.();
            } catch (error) {
                // The draft is deliberately left in the textarea (it is only
                // cleared on success) so the coordinator can retry without
                // retyping. Without this toast a dropped request is
                // indistinguishable from a successful send.
                console.error('Failed to send message:', error);
                toast.error('Couldn’t send message', {
                    persistNotification: false,
                    description: 'Check your connection and try again.',
                });
            } finally {
                setIsSending(false);
                if (restoreFocusAfterSendRef.current) {
                    restoreFocusAfterSendRef.current = false;
                    window.requestAnimationFrame(() => {
                        const activeElement = document.activeElement;
                        if (
                            activeElement === null ||
                            activeElement === document.body ||
                            formRef.current?.contains(activeElement)
                        ) {
                            textareaRef.current?.focus();
                        }
                    });
                }
            }
        },
        [
            analyticsSurface,
            attachmentIds,
            clearImages,
            conversationId,
            draft,
            organizationId,
            content,
            mode,
            onSent,
            posthog,
            totalSizeBytes,
        ],
    );

    const sendMessage = useCallback(async () => {
        if (draft?.sendDisabled) {
            return;
        }
        if (
            isSending ||
            isRecording ||
            isTranscribing ||
            translationPreview !== null
        ) {
            return;
        }

        if (
            (!content.trim() && !attachmentIds.length) ||
            isUploading ||
            hasFailedUploads
        ) {
            return;
        }

        if (content.length > MESSAGE_MAX_LENGTH) {
            return;
        }

        const activeElement = document.activeElement;
        restoreFocusAfterSendRef.current =
            activeElement === null ||
            activeElement === document.body ||
            Boolean(formRef.current?.contains(activeElement));

        if (!content.trim()) {
            await doSend('', null);
            return;
        }

        if (draft && draft.caregiverId === null) {
            // A bare phone number has no caregiver whose language we could
            // look up, so there is nothing to preview.
            await doSend(content, null);
            return;
        }

        // Ask the API whether this caregiver needs a translation. On any error
        // we fall through and send the original English (today's behavior).
        const generation = (previewGenerationRef.current += 1);
        setIsSending(true);
        let preview = null;
        try {
            preview = await fetchOutboundTranslationPreview({
                organizationId,
                content,
                ...(draft
                    ? { caregiverId: draft.caregiverId ?? undefined }
                    : { conversationId }),
            });
        } catch (error) {
            console.error('Failed to translate message preview:', error);
        }

        // Discard the result if the composer unmounted or switched
        // conversations mid-fetch, so we never send to the wrong thread.
        if (previewGenerationRef.current !== generation) {
            return;
        }

        if (preview?.translated_text && preview.target_language_code) {
            setTranslationPreview({
                englishSource: content,
                languageName: preview.target_language_name,
                translatedText: preview.translated_text,
            });
            setIsSending(false);
            return;
        }

        await doSend(content, null);
    }, [
        content,
        conversationId,
        doSend,
        draft,
        attachmentIds,
        isRecording,
        isSending,
        isTranscribing,
        hasFailedUploads,
        isUploading,
        organizationId,
        translationPreview,
    ]);

    const handleRecordingStop = useCallback(
        async (mimeType: string) => {
            clearRecordingTimeout();
            setIsRecording(false);
            mediaRecorderRef.current = null;

            const draftLength = content.trim().length;
            const stopReason = recordingStopReasonRef.current;
            const recordingDurationMs =
                recordingStartedAtRef.current === null
                    ? undefined
                    : Math.round(
                          performance.now() - recordingStartedAtRef.current,
                      );
            recordingStartedAtRef.current = null;
            const audioChunks = [...audioChunksRef.current];
            audioChunksRef.current = [];
            stopMediaStream();
            const audioBlob = new Blob(audioChunks, { type: mimeType });
            const surface = analyticsSurface ?? detectMessageComposerSurface();
            const source = getMessageComposerSource(surface);

            posthog?.capture(EVENTS.VOICE_INPUT_RECORDING_STOPPED, {
                audio_size_bytes: audioBlob.size,
                composer: 'message_composer',
                conversation_id: conversationId,
                draft_length: draftLength,
                has_existing_draft: draftLength > 0,
                mode,
                organization_id: organizationId,
                recording_duration_ms: recordingDurationMs,
                source,
                stop_reason: stopReason,
                surface,
                voice_input_flow: 'conversation_messages',
            });

            if (!audioChunks.length) {
                posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                    audio_size_bytes: audioBlob.size,
                    composer: 'message_composer',
                    conversation_id: conversationId,
                    draft_length: draftLength,
                    has_existing_draft: draftLength > 0,
                    mode,
                    organization_id: organizationId,
                    recording_duration_ms: recordingDurationMs,
                    source,
                    stage: 'empty_audio',
                    stop_reason: stopReason,
                    surface,
                    voice_input_flow: 'conversation_messages',
                });
                setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
                return;
            }

            setIsTranscribing(true);
            const generation = transcriptionGenerationRef.current;
            try {
                const transcript = await transcribeAudioBlob({
                    audioBlob,
                    mode,
                    organizationId,
                });
                if (generation !== transcriptionGenerationRef.current) {
                    return;
                }
                const trimmedTranscript = transcript.trim();
                if (!trimmedTranscript) {
                    posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                        audio_size_bytes: audioBlob.size,
                        composer: 'message_composer',
                        conversation_id: conversationId,
                        draft_length: draftLength,
                        has_existing_draft: draftLength > 0,
                        mode,
                        organization_id: organizationId,
                        recording_duration_ms: recordingDurationMs,
                        source,
                        stage: 'empty_transcript',
                        stop_reason: stopReason,
                        surface,
                        voice_input_flow: 'conversation_messages',
                    });
                    setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
                    return;
                }

                if (content.length >= MESSAGE_MAX_LENGTH) {
                    // Appending would discard the whole transcript, so
                    // surface an error instead of silently dropping it.
                    posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                        audio_size_bytes: audioBlob.size,
                        composer: 'message_composer',
                        conversation_id: conversationId,
                        draft_length: draftLength,
                        has_existing_draft: draftLength > 0,
                        mode,
                        organization_id: organizationId,
                        recording_duration_ms: recordingDurationMs,
                        source,
                        stage: 'character_limit',
                        stop_reason: stopReason,
                        surface,
                        voice_input_flow: 'conversation_messages',
                    });
                    setComposerInputError(MESSAGE_INPUT_LIMIT_ERROR);
                    return;
                }

                draftIncludesVoiceInputRef.current = true;
                setComposerInputError(null);
                setVoiceInputError(null);
                const nextContent = appendTranscribedText(content, transcript);
                posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_SUCCEEDED, {
                    appended_to_existing_draft: draftLength > 0,
                    audio_size_bytes: audioBlob.size,
                    composer: 'message_composer',
                    conversation_id: conversationId,
                    draft_length: draftLength,
                    draft_length_after_append: nextContent.trim().length,
                    mode,
                    organization_id: organizationId,
                    recording_duration_ms: recordingDurationMs,
                    source,
                    stop_reason: stopReason,
                    surface,
                    transcript_length: trimmedTranscript.length,
                    transcript_word_count:
                        trimmedTranscript.split(/\s+/).length,
                    voice_input_flow: 'conversation_messages',
                });
                setContent((currentContent) => {
                    const next = appendTranscribedText(
                        currentContent,
                        transcript,
                    );
                    return next.slice(0, MESSAGE_MAX_LENGTH);
                });
                // Voice input updates the draft via setContent (not the textarea
                // onChange), so signal typing here too or voice composers stay
                // invisible to teammates' typing indicator.
                if (nextContent.trim()) {
                    onTyping?.();
                }
                window.requestAnimationFrame(() => {
                    textareaRef.current?.focus();
                    resizeMessageTextarea(textareaRef.current);
                });
            } catch (error) {
                console.error('Failed to transcribe audio:', error);
                posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                    audio_size_bytes: audioBlob.size,
                    composer: 'message_composer',
                    conversation_id: conversationId,
                    draft_length: draftLength,
                    error_message:
                        error instanceof Error ? error.message : undefined,
                    has_existing_draft: draftLength > 0,
                    mode,
                    organization_id: organizationId,
                    recording_duration_ms: recordingDurationMs,
                    source,
                    stage: 'transcription_request',
                    stop_reason: stopReason,
                    surface,
                    voice_input_flow: 'conversation_messages',
                });
                setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
            } finally {
                setIsTranscribing(false);
            }
        },
        [
            analyticsSurface,
            clearRecordingTimeout,
            content,
            conversationId,
            mode,
            onTyping,
            organizationId,
            posthog,
            stopMediaStream,
        ],
    );

    const startRecording = useCallback(async () => {
        if (!canRecordAudio || isSending || isTranscribing || isRecording) {
            return;
        }

        const draftLength = content.trim().length;
        const surface = analyticsSurface ?? detectMessageComposerSurface();
        const source = getMessageComposerSource(surface);
        setVoiceInputError(null);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            mediaStreamRef.current = stream;
            const mimeType = getPreferredRecordingMimeType();
            const recorder = mimeType
                ? new MediaRecorder(stream, { mimeType })
                : new MediaRecorder(stream);
            const recorderMimeType =
                recorder.mimeType || mimeType || 'audio/webm';

            mediaRecorderRef.current = recorder;
            audioChunksRef.current = [];
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            recorder.onstop = () => {
                void handleRecordingStop(recorderMimeType);
            };
            recorder.start();

            recordingStartedAtRef.current = performance.now();
            recordingStopReasonRef.current = 'manual';
            setIsRecording(true);
            posthog?.capture(EVENTS.VOICE_INPUT_RECORDING_STARTED, {
                composer: 'message_composer',
                conversation_id: conversationId,
                draft_length: draftLength,
                has_existing_draft: draftLength > 0,
                mode,
                organization_id: organizationId,
                source,
                surface,
                voice_input_flow: 'conversation_messages',
            });
            recordingTimeoutRef.current = window.setTimeout(() => {
                if (recorder.state !== 'recording') {
                    return;
                }

                recordingStopReasonRef.current = 'max_duration';
                recorder.stop();
            }, MAX_RECORDING_DURATION_MS);
        } catch (error) {
            console.error('Failed to start recording:', error);
            const permissionDenied = isMicrophonePermissionError(error);
            posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                composer: 'message_composer',
                conversation_id: conversationId,
                draft_length: draftLength,
                error_message:
                    error instanceof Error ? error.message : undefined,
                has_existing_draft: draftLength > 0,
                mode,
                organization_id: organizationId,
                source,
                stage: permissionDenied
                    ? 'permission_denied'
                    : 'recording_start',
                surface,
                voice_input_flow: 'conversation_messages',
            });
            setVoiceInputError(
                permissionDenied
                    ? VOICE_INPUT_PERMISSION_ERROR_MESSAGE
                    : VOICE_INPUT_ERROR_MESSAGE,
            );
            stopMediaStream();
            mediaRecorderRef.current = null;
        }
    }, [
        analyticsSurface,
        canRecordAudio,
        content,
        conversationId,
        handleRecordingStop,
        isRecording,
        isSending,
        isTranscribing,
        mode,
        organizationId,
        posthog,
        stopMediaStream,
    ]);

    const stopRecording = useCallback(() => {
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state !== 'recording') {
            return;
        }

        recordingStopReasonRef.current = 'manual';
        recorder.stop();
    }, []);

    return (
        <div className="flex justify-end">
            <form
                ref={formRef}
                className="w-full space-y-2"
                onSubmit={(event) => {
                    event.preventDefault();
                    sendMessage();
                }}
                onPaste={handlePaste}
            >
                <EmojiEnabledTextarea
                    className="border-border relative rounded-lg border-1 p-3"
                    content={content}
                    maxLength={MESSAGE_MAX_LENGTH}
                    onContentChange={handleComposerContentChange}
                    onInsertionLimit={handleEmojiInsertionLimit}
                    onInteraction={() => {
                        setComposerInputError(null);
                    }}
                    onTextareaKeyDown={(event) => {
                        if (isSending || isRecording || isTranscribing) {
                            return;
                        }

                        if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            sendMessage();
                        }
                    }}
                    pickerDisabled={
                        isSending ||
                        isRecording ||
                        isTranscribing ||
                        translationPreview !== null
                    }
                    resetKey={conversationId}
                    textareaProps={{
                        className:
                            'min-h-[2.5rem] w-full resize-none border-0 bg-transparent p-0 shadow-none focus-visible:ring-0',
                        disabled: isSending || isRecording || isTranscribing,
                        onInput: (event) => {
                            resizeMessageTextarea(
                                event.target as HTMLTextAreaElement,
                            );
                        },
                        placeholder: isRecording
                            ? 'Listening…'
                            : 'Write a message…',
                        rows: 1,
                        style: {
                            height: 'auto',
                            overflow: 'hidden',
                        },
                    }}
                    textareaRef={textareaRef}
                >
                    {({ emojiPicker, textarea }) => (
                        <Fragment>
                            {textarea}
                            <MessageImageUploadControls
                                disabled={
                                    isSending ||
                                    isRecording ||
                                    isTranscribing ||
                                    translationPreview !== null
                                }
                                onAddFiles={addFiles}
                                onRemove={removeUpload}
                                uploads={uploads}
                            >
                                {emojiPicker}
                                <div className="flex-1" />
                                {canRecordAudio ? (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size={isTranscribing ? 'sm' : 'icon'}
                                        className={
                                            isTranscribing
                                                ? 'h-8 flex-shrink-0 self-center rounded-full px-3 text-xs font-medium'
                                                : 'h-8 w-8 flex-shrink-0 self-center rounded-full'
                                        }
                                        aria-label={
                                            isTranscribing
                                                ? 'Transcribing voice message'
                                                : isRecording
                                                  ? 'Stop recording'
                                                  : 'Record voice message'
                                        }
                                        disabled={isSending || isTranscribing}
                                        onClick={() => {
                                            if (isRecording) {
                                                stopRecording();
                                                return;
                                            }

                                            void startRecording();
                                        }}
                                    >
                                        {isTranscribing ? (
                                            <span className="flex items-center gap-1.5">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                <span>Transcribing</span>
                                            </span>
                                        ) : (
                                            <span className="relative flex items-center justify-center">
                                                <Mic className="h-4 w-4" />
                                                {isRecording ? (
                                                    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 animate-pulse rounded-full bg-red-500" />
                                                ) : null}
                                            </span>
                                        )}
                                    </Button>
                                ) : null}
                                <Button
                                    type="submit"
                                    disabled={
                                        draft?.sendDisabled ||
                                        isSending ||
                                        isRecording ||
                                        isTranscribing ||
                                        isUploading ||
                                        hasFailedUploads ||
                                        (!content.trim() &&
                                            !attachmentIds.length)
                                    }
                                    size="icon"
                                    className="h-8 w-8 flex-shrink-0 self-center rounded-full"
                                    aria-label="Send message"
                                >
                                    {isSending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <ArrowUp className="h-4 w-4" />
                                    )}
                                </Button>
                            </MessageImageUploadControls>
                            {composerInputError ? (
                                <p className="text-destructive mt-2 text-xs">
                                    {composerInputError}
                                </p>
                            ) : null}
                            {voiceInputError ? (
                                <p className="text-destructive mt-2 text-xs">
                                    {voiceInputError}
                                </p>
                            ) : null}
                            {content.length >= MESSAGE_COUNTER_VISIBLE_FROM ? (
                                <p
                                    className={cn(
                                        'text-xs',
                                        content.length >= MESSAGE_MAX_LENGTH
                                            ? 'text-destructive'
                                            : 'text-muted-foreground',
                                    )}
                                >
                                    {content.length}/{MESSAGE_MAX_LENGTH}
                                </p>
                            ) : null}
                        </Fragment>
                    )}
                </EmojiEnabledTextarea>
            </form>
            {translationPreview !== null ? (
                <TranslationPreviewDialog
                    englishSource={translationPreview.englishSource}
                    isSending={isSending}
                    languageName={translationPreview.languageName}
                    onCancel={() => {
                        restoreFocusAfterSendRef.current = false;
                        setTranslationPreview(null);
                        window.requestAnimationFrame(() => {
                            textareaRef.current?.focus();
                        });
                    }}
                    onSendEnglish={() => {
                        void doSend(translationPreview.englishSource, null);
                    }}
                    onSendTranslated={(translatedBody) => {
                        void doSend(
                            translatedBody,
                            translationPreview.englishSource,
                        );
                    }}
                    open={true}
                    translatedText={translationPreview.translatedText}
                />
            ) : null}
        </div>
    );
}
